import { CMSPilotApis } from "apis/cmsPilotAPI";
import { useGlobalConfig } from "context/GlobalContext";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectCMSSelectedPilotDocId, selectCMSSelectedPilot } from "store/dataReducers/cmsPilotSlice";
import { GetCMSPilotStats } from "types/apis/cmsPilotInterfaces";
import PostAddIcon from '@mui/icons-material/PostAdd';
import BusinessIcon from '@mui/icons-material/Business';
import MicrowaveIcon from '@mui/icons-material/Microwave';
import RecommendIcon from '@mui/icons-material/Recommend';
import GradingIcon from '@mui/icons-material/Grading';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { ListTree } from "lucide-react"
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Grid,
  Button,
  useTheme
} from '@mui/material';
import { ROUTE_TREE } from "../../routes/AppRoutes";
import TranslateIcon from '@mui/icons-material/Translate';
import { MigrationImportModal } from "features/migration";

export const CMSPilotContentPage = () => {

  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const [stats, setStats] = useState<GetCMSPilotStats.retval>();
  const [importModalOpen, setImportModalOpen] = useState(false);
  const selectedPilotDocId = useSelector(selectCMSSelectedPilotDocId);
  const selectedPilot = useSelector(selectCMSSelectedPilot);

  const SUPPORTED_OPTIONS = [
    {
      id: 'HOME_PROFILE',
      title: 'Home Profile',
      icon: <BusinessIcon />,
      route: ROUTE_TREE.CMS.HOME_PROFILE
    },
    {
      id: 'APPLIANCE_PROFILE',
      title: 'Appliance Profile',
      icon: <MicrowaveIcon />,
      route: ROUTE_TREE.CMS.APPLIANCE_PROFILE
    },
    {
      id: 'RECO_SCORE',
      title: 'Recommendation Score Thresholds',
      icon: <GradingIcon />,
      route: ROUTE_TREE.CMS.RECO.SCORE
    },
    {
      id: 'RECOMMENDATIONS',
      title: 'Recommendations',
      icon: <RecommendIcon />,
      route: ROUTE_TREE.CMS.RECO.LIST
    },
    {
      id: 'SURVEY_TEMPLATES',
      title: 'Survey Templates',
      icon: <ListTree />,
      route: ROUTE_TREE.CMS.SURVEY.HOME
    },
    ...(rc.permissionsManager.canStringRegistryView()
      ? [{
          id: 'STRING_REGISTRY',
          title: 'String Registry',
          icon: <TranslateIcon />,
          route: ROUTE_TREE.CMS.STRING_REGISTRY
        }]
      : [])
  ]

  useEffect(() => {
    const getUtilityStats = async () => {
      const resp = await CMSPilotApis.getPilotStats(rc, selectedPilotDocId);
      if (resp.data) {
        setStats(resp.data);
      }
    };

    getUtilityStats();


  }, [rc, selectedPilotDocId]);

  const getCountForOption = (id: string): number => {
    if (!stats) return 0;
    const stat = stats.find(s => s.id === id);
    return stat ? stat.count : 0;
  };

  const handleOptionClick = (route: { path: string; name: string }) => {
    rc.uiRouter.navigate(route);
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Card sx={{ 
        margin: '0 auto',
        borderRadius: 2
      }}>
        <CardContent sx={{ padding: 2 }}>
          <Box sx={{ marginBottom: 4 }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PostAddIcon sx={{
                  fontSize: 30,
                  marginRight: 1,
                  color: theme.palette.primary.main
                }} />
                <Typography variant="h6" sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.secondary
                }}>
                  Content Management System
                </Typography>
              </Box>

              {rc.permissionsManager.canMigrationImport() &&
                stats !== undefined &&
                getCountForOption('RECOMMENDATIONS') === 0 && (
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setImportModalOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Import from Carrom
                </Button>
              )}
            </Box>

            <Typography variant="body1" sx={{
              textAlign: 'left',
              fontSize: '14px',
              lineHeight: 1.5,
              marginBottom: 1,
              color: theme.palette.text.secondary
            }}>
              Update Next Best Interactions, personalized recommendations, and other static CX content seamlessly
            </Typography>
          </Box>

          {/* Grid Options */}
          <Grid container spacing={2}>
            {stats && stats.map((stat, index) => {
              
              const option = SUPPORTED_OPTIONS.find(o => o.id === stat.id);
              if (!option) throw new Error(`Option not found for id: ${stat.id}`);

              return (
              <Grid size={{ xs: 6 }} key={index}>
                <Box 
                  sx={{ 
                    cursor: 'pointer',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    backgroundColor: theme.palette.background.paper,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 2px 8px ${theme.palette.primary.main}25`,
                      transform: 'translateY(-1px)'
                    }
                  }}
                  onClick={() => handleOptionClick(option.route)}
                >
                  <Box sx={{ 
                    padding: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ 
                        marginRight: 2,
                        color: theme.palette.primary.main
                      }}>
                        {option.icon}
                      </Box>
                      <Typography variant="body1" sx={{ 
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                        fontSize: '16px'
                      }}>
                        {option.title}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ 
                      backgroundColor: theme.palette.action.hover,
                      borderRadius: '12px',
                      padding: '4px 12px',
                      minWidth: '32px',
                      textAlign: 'center'
                    }}>
                      <Typography variant="body2" sx={{ 
                        color: theme.palette.text.secondary,
                        fontWeight: 600,
                        fontSize: '14px'
                      }}>
                        {getCountForOption(stat.id)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            )})}
          </Grid>
        </CardContent>
      </Card>

      {/* Migration Import Modal */}
      {selectedPilot && (
        <MigrationImportModal
          open={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          pilotId={selectedPilot.pilotId}
          supportedLocales={
            Array.isArray(selectedPilot.supportedLocales)
              ? (selectedPilot.supportedLocales as string[])
              : undefined
          }
          onMigrationComplete={() => {
            // Refresh stats after migration completes
            CMSPilotApis.getPilotStats(rc, selectedPilotDocId).then(resp => {
              if (resp.data) setStats(resp.data);
            });
          }}
        />
      )}
    </Box>
  );
};